
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import UPDATE_USER_APPLICATION from "@/apollo/jobboard/UpdateUserApplication.gql";

import {
  User,
  Company,
  Application_Set_Input,
  Document,
  Posting,
  Application,
} from "@/generated/graphql";
import TableListChipNew from "@/components/table/table-list-chip-new.vue";
import RatingStars from "@/components/rating/RatingStars.vue";

import CardBodyItem from "@/components/card/CardBodyItem.vue";
import Card from "@/components/card/Card.vue";
import Chip from "@/components/filters/Chip.vue";
import TableIcon from "@/components/table/table-icon.vue";
import RouteNames from "@/entities/RouteNames";
import GoToInbox from "@/components/common/GoToInbox.vue";
import CardAvatar from "@/components/card/CardAvatar.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import ApplicantStatusSelect from "@/components/jobboard/ApplicantStatusSelect.vue";
import Message from "@/data/notificationsMessages";
import UiButton from "@/components/button/ui-button.vue";
import InterviewButton from "@/components/applications/InterviewButton.vue";
import AddContractorToCompany from "@/components/contractors/AddContractorToCompany.vue";
import ManagerContractorRequest from "@/components/contractors/ManagerContractorRequest.vue";
import Badge from "@/components/common/Badge.vue";
import CardInfoChip from "@/components/card/CardInfoChip.vue";

import belongsToCompany, {
  getContractorIfInSameCompany,
} from "@/helpers/isContractorInCompany";

import BadgeColor from "@/entities/BadgeColor";
import RouteParams from "@/entities/RouteParams";
import ItemChipsData from "@/entities/ItemChipsData";
import { ApplicantStatus } from "@/entities/ApplicantStatus";
import {
  applicantStatusNames,
  applicantStatusNamesToValues,
} from "@/entities/ApplicantStatus";
import DropdownOption from "@/entities/DropdownOption";
import { NOTIFICATION } from "@/entities/Events";
import { NO_INFORMATION } from "@/data/placeholders";
import Roles from "@/entities/Roles";
import CollapsibleDescription from "@/components/common/CollapsibleDescription.vue";
import DocumentProvider from "@/components/documents/DocumentProvider.vue";
import ColorIconContainer from "@/components/icons/ColorIconContainer.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import getFullName from "@/helpers/getFullName";
import ChatIconNew from "../icons/ChatIconNew.vue";
import MessageIcon from "../icons/MessageIcon.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import UserIcon from "@/components/icons/UserFill.vue";
import DollarIcon from "@/components/icons/dollar-symbol.vue";
import FileAttachmentIcon from "../icons/FileAttachmentIcon.vue";
import AppliedIcon from "../icons/AppliedIcon.vue";
import NoteDisplayModel from "@/components/applications/NoteDisplayModel.vue";
import { getPredefindMessageKey } from "@/utils";

@Component({
  components: {
    DownloadIcon,
    ColorIconContainer,
    DocumentProvider,
    RatingStars,
    CollapsibleDescription,
    TableListChipNew,
    CardBodyItem,
    FileAttachmentIcon,
    NoteDisplayModel,
    AppliedIcon,
    MessageIcon,
    Chip,
    Card,
    CardAvatar,
    TableIcon,
    UserIcon,
    GoToInbox,
    ApplicantStatusSelect,
    UiButton,
    ItemChipsWithMore,
    AddContractorToCompany,
    ManagerContractorRequest,
    InterviewButton,
    Badge,
    CardInfoChip,
    ChatIconNew,
    LocationIcon,
    DollarIcon,
  },
})
export default class ApplicationCard extends Vue {
  @Prop({ type: Object, required: true }) readonly user!: User;
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Object, default: () => ({}) })
  readonly attachedDocument!: Document;
  @Prop({ type: String, required: true }) readonly applicationId!: string;
  @Prop({ type: String, required: true }) readonly status!: ApplicantStatus;
  @Prop({ type: String, required: true }) readonly jobTitle!: string;
  @Prop({ type: String, default: "" }) readonly interviewedBy!: string;
  @Prop({ type: Object, default: undefined }) readonly interviewer?: User;
  @Prop({ type: Object, default: undefined })
  readonly application?: Application;
  @Prop({ type: Boolean, default: false })
  readonly isApplicationCancelled!: boolean;
  @Prop({ type: String, default: undefined }) readonly note?: string;
  notedialogOpen = false;

  showUpdateButton: boolean = false;
  statusDropdownValue: DropdownOption[] = [
    { name: this.applicantStatus, id: this.status },
  ];
  disableButton: boolean = true;
  processing: boolean = false;
  BadgeColor: typeof BadgeColor = BadgeColor;

  onMessageLinkedClicked(event, id) {
    window.sessionStorage.setItem(
      getPredefindMessageKey(id),
      `Hello, ${this.userFullName}! Thank you for applying for ${this.jobTitle} position. We would like to invite you to an interview.`
    );
  }
  get getNoteTitle() {
    return `${this.userFullName} Comments`;
  }
  get badgeData(): { value: string; color?: string; class?: string } {
    if (this.isApplicationWithdrawn) {
      return {
        value: "Withdrawn",
        class: `b-withdraw p-4 text-sm rounded-full capitalize`,
      };
    }
  if(this.applicationDeclined){
     return {
        value: "declined",
        class: `b-declined p-4 text-sm rounded-full capitalize`,
      };
  }
  if(this.applicationCancelled){
     return {
        value: "cancelled",
        class: `b-cancelled p-4 text-sm rounded-full capitalize`,
      };
  }
    return {
      value: this.applicantStatus,
      class: `b-${
        applicantStatusNamesToValues[this.applicantStatus]
      } p-4 text-sm rounded-full capitalize`,
    };
  }

  @Watch("statusDropdownValue")
  checkValueChanged(newVal: DropdownOption[]) {
    if (!newVal[0]) {
      this.disableButton = true;
    } else if (newVal[0].id != this.status) {
      this.disableButton = false;
    } else {
      this.disableButton = true;
    }
  }

  get isInvitedToInterview(): boolean {
    return Boolean(this.interviewedBy);
  }

  get showInterviewerInfo(): boolean {
    return this.isInvitedToInterview && !this.isApplicantDeclined;
  }

  get showInterviewerText(): boolean {
    return this.isManager || this.isCurrentUserInterviewer;
  }

  get showInterviewerLink(): boolean {
    return this.isAdmin && this.isInterviewerManager;
  }

  get showInterviewButton(): boolean {
    return (
      !this.isApplicationWithdrawn &&
      !this.isInvitedToInterview &&
      !this.isApplicantDeclined
    );
  }

  get isInterviewerManager(): boolean {
    return Boolean(this.interviewer?.role === Roles.MANAGER);
  }

  get isCurrentUserInterviewer(): boolean {
    return Boolean(this.interviewer?.id === this.currentUser.id);
  }
  get getContractorIfInSameComapny(): any {
    const contract = getContractorIfInSameCompany(this.user);
    if (contract) {
      return {
        ...contract,
        rating:
          (contract as any)?.contractor_ratings_aggregate?.aggregate?.avg
            ?.score || 0,
      };
    }
    return false;
  }
  get invitedToInterviewBadgeText(): string {
    if (!this.interviewer) {
      return "";
    }
    const { given_name: name, family_name: surname } = this.interviewer!;
    const fullName = getFullName(name!, surname!);

    if (this.showInterviewerText) {
      return this.isCurrentUserInterviewer
        ? "Interview with you"
        : this.isAdmin
        ? fullName
        : `Interview with <b>${fullName}</b>`;
    }
    return fullName;
  }

  get managerDetailsLink(): RouteParams<{ id: string }> | undefined {
    if (!this.interviewer?.manager) {
      return undefined;
    }
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: this.interviewer!.manager!.id },
    };
  }

  get attachedDocumentName(): string {
    return this.attachedDocument?.filename || "";
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get managerId(): string | undefined {
    return this.currentUser.manager?.id;
  }

  get userAvatar(): string {
    return this.user.avatar || "";
  }

  get userInitials(): string {
    return `${this.user.given_name![0]}${this.user.family_name![0]}`;
  }

  get userFullName(): string {
    return `${this.user.given_name} ${this.user.family_name}`;
  }

  get userOccupation(): string {
    return this.user.profile?.occupation || NO_INFORMATION;
  }

  get userLocation(): string {
    if (!this.user.profile) {
      return NO_INFORMATION;
    }
    const { country, state, city } = this.user.profile;
    if (!country && !state && !city) {
      return NO_INFORMATION;
    }
    return `${city}, ${state}, ${country}`;
  }

  get userSkills(): ItemChipsData[] {
    if (!this.user.profile?.skills?.length) {
      return [];
    }
    const skills = this.user.profile!.skills;
    return skills.map(({ skill }) => ({
      id: skill.id,
      name: skill.name!,
    }));
  }

  get userRates(): string {
    const { profile } = this.user;
    const { min_rate = 0, max_rate = 0, currency = "", rate_unit } = profile!;
    const rateUnit = rate_unit ? `/ ${rate_unit}` : "";
    if (min_rate && max_rate) {
      return `$${min_rate} - $${max_rate} ${currency} ${rateUnit}`;
    }
    if (min_rate && !max_rate) {
      return `from $${min_rate} ${currency} ${rateUnit}`;
    }
    if (!min_rate && max_rate) {
      return `up to $${max_rate} ${currency} ${rateUnit}`;
    }

    return 'n/a';
  }

  get applicantStatus(): string {
    return applicantStatusNames[this.status];
  }

  get isApproved(): boolean {
    return this.applicantStatus.toLowerCase() == ApplicantStatus.APPROVED;
  }

  get isApplicationWithdrawn(): boolean {
    return this.isApplicationCancelled;
  }

  get applicationDeclined(): boolean {
    if(this.application.status === 'declined'){
      return true;
    }
    return;
  }

 get applicationCancelled(): boolean {
    if(this.application.status === 'cancelled'){
      return true;
    }
    return;
  }

  get isApplicantDeclined(): boolean {
    return this.applicantStatus.toLowerCase() === ApplicantStatus.DECLINED;
  }

  get applicantProfileRoute(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
      params: { id: this.user.id },
    };
  }

  get showAddContractorButton(): boolean {
    return !belongsToCompany(this.user) && this.isAdmin;
  }

  statusChosen(): void {
    this.showUpdateButton = true;
  }

  handleRefetchCollection(): void {
    this.$emit("collectionChanged");
  }

  async updateApplicantStatus(): Promise<void> {
    this.processing = true;
    const status = this.statusDropdownValue[0].id;
    const set: Application_Set_Input = {
      status,
    };

    try {
      await this.updateApplication(set);
      this.$emit("collectionChanged");
      this.disableButton = true;
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    } finally {
      this.processing = false;
    }
  }

  async updateApplication(set: Application_Set_Input): Promise<void> {
    await this.$apollo.mutate({
      mutation: UPDATE_USER_APPLICATION,
      variables: {
        where: {
          id: { _eq: this.applicationId },
        },
        set,
      },
    });
  }
}
